import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TabButton from '../components/benefits/tabButton';
import BenefitCard from '../components/benefits/cardBenefit';
import useRequestState from '../hooks/useRequestState';
import GetBenefitCardData from '../services/adapters/benefitCards';
import { BenefitResponse } from '../types/global';
import BenefitGraph from '../components/benefits/graphBenefit';
import { All } from '../types/enums';
import useSnapshot from '../utils/snapshot';
import useSnapshotWardFilters from '../hooks/useSnapshotWardFilters';
import SnapshotWardDropdown from '../components/snapshotWardDropdown';

export default function Benefits() {
  const { snapshot } = useSnapshot();
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(All.label);

  const headlinePrompt = 'Open the Details sections to identify extra areas for potential impact.';
  const {
    selectedSnapshot,
    selectedLocalAuthority,
    selectedWard,
    handleSnapshotChange,
    handleLocalAuthorityChange,
    handleWardChange,
  } = useSnapshotWardFilters(snapshot);
  const {
    data: benefitResponse,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<BenefitResponse>();
  const titles = benefitResponse ? [All.label, ...Array.from(new Set(benefitResponse.cards.map((card) => card.stage)))] : [All.label];

  useEffect(() => {
    const fetchBenefitCards = async () => {
      const benefitCardData = new GetBenefitCardData();
      const response = benefitCardData.adaptResponse({ snapshot: selectedSnapshot.value, liftDataId: selectedLocalAuthority.value, wardName: selectedWard.value });
      await handleRequest(response);
    };
    void fetchBenefitCards();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSnapshot, selectedLocalAuthority, selectedWard]);
  const options = benefitResponse
    ? benefitResponse.cards.filter((card) => activeTab === All.label || card.stage === activeTab).map((card) => ({ label: card.displayName, value: card.cardKey }))
    : [];

  const headlineMessage = benefitResponse ? benefitResponse.stageText[activeTab] : '';

  return (
    <div id="benefits-page" className="flex flex-col w-full gap-4 px-2 m-4 overflow-scroll">
      <header id="benefit-page-header" className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <h1>Benefit take up</h1>
        </div>
        <span id="benefit-page-description" className="text-pipgrey text-sm font-normal">
          {headlinePrompt}
        </span>
        <SnapshotWardDropdown
          selectedSnapshot={selectedSnapshot || { label: snapshot.formattedSnapshot, value: snapshot.rawSnapshot }}
          selectedWard={selectedWard}
          selectedLocalAuthority={selectedLocalAuthority}
          onSnapshotChange={handleSnapshotChange}
          onWardChange={handleWardChange}
          onLocalAuthorityChange={handleLocalAuthorityChange}
        />
      </header>
      <div>
        <div id="areas-section" className="self-stretch flex justify-start items-center gap-6">
          <div className="justify-start font-semibold text-secondary">Areas</div>
          <div className="flex-1 border-t border-pipgrey-light" />
        </div>
        <TabButton theme={titles} activeTab={activeTab} setActiveTab={setActiveTab} />
        <div className="text-xs md:text-sm p-2 benefits-markdown">
          <ReactMarkdown className="max-w-3/4" remarkPlugins={[remarkGfm]}>{headlineMessage}</ReactMarkdown>
        </div>
      </div>
      <FallbackWrapper loadingMessage="loading...">
        <div id="benefit-cards-container" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-5">
          { benefitResponse && benefitResponse.cards.filter((item) => (activeTab === All.label ? true : item.stage === activeTab)).map((benefit) => (
            <BenefitCard
              key={benefit.cardKey}
              title={benefit.displayName}
              data={benefit.areas}
              isExpanded={expandedCard === benefit.displayName}
              onToggle={(id) => setExpandedCard(expandedCard === id ? null : id)}
            />
          ))}
        </div>
      </FallbackWrapper>
      <BenefitGraph liftDataId={selectedLocalAuthority.value} wardName={selectedWard.value} options={options} />
    </div>
  );
}
